import { motion } from "framer-motion";
import { ArrowDownToLine, Cpu, Send } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const STEPS = [
  {
    num: "01",
    icon: ArrowDownToLine,
    title: "Ingest",
    body: "Drop files straight into the workspace — PDFs, scans, images. Batch-ready up to 50MB a file, with live page previews before anything runs.",
    testid: "workflow-step-ingest",
  },
  {
    num: "02",
    icon: Cpu,
    title: "Execute",
    body: "Sixteen engines run in seconds: compress, merge, redact, protect, edit. Every action is previewable, reversible, and leaves no trace on our side.",
    testid: "workflow-step-execute",
  },
  {
    num: "03",
    icon: Send,
    title: "Deliver",
    body: "Download client-ready output, send it for e-signature, or invoice the work on the spot — the whole loop closes without leaving the page.",
    testid: "workflow-step-deliver",
  },
];

export const Workflow = () => (
  <section className="py-24 md:py-32 lg:py-40" data-testid="workflow-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-24">
        <motion.div
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: EASE }}
          className="lg:col-span-8"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-5">03 — Workflow</span>
          <h2 className="font-medium tracking-tight leading-tight text-3xl md:text-4xl lg:text-5xl text-ink max-w-2xl" data-testid="workflow-title">
            From inbox to executed agreement.
          </h2>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: EASE, delay: 0.1 }}
          className="lg:col-span-4 text-base text-fog leading-relaxed self-end"
        >
          Upload, run, deliver — three steps with no busywork, so the deal never waits on paperwork.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.2, ease: EASE }}
          className="lg:sticky lg:top-28 self-start"
        >
          <div className="relative rounded-xl overflow-hidden border border-hairline" data-testid="workflow-visual">
            <img src="/hero-bg.png" alt="" aria-hidden="true" className="w-full h-[320px] md:h-[420px] object-cover" />
            <div className="absolute inset-0 bg-[#0C0A09]/60" aria-hidden="true" />
            <div className="absolute bottom-0 inset-x-0 p-6 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/90">Document pipeline</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">Zero retention</span>
            </div>
          </div>
        </motion.div>

        <div className="space-y-20 md:space-y-32 lg:py-16">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: EASE, delay: 0.05 * i }}
              className="flex items-start gap-6"
              data-testid={s.testid}
            >
              <span className="font-mono text-sm text-mist pt-2 w-8 shrink-0">{s.num}</span>
              <div>
                <span className="w-11 h-11 rounded-full border border-hairline flex items-center justify-center mb-5">
                  <s.icon className="w-5 h-5 text-azure" strokeWidth={1.5} />
                </span>
                <h3 className="font-medium tracking-tight text-2xl text-ink mb-3">{s.title}</h3>
                <p className="text-base text-fog leading-relaxed max-w-md">{s.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
